import React from "react";
import { api } from "./api.jsx";
import { useApp } from "./store.jsx";
import { GAMES } from "./data.jsx";
import { DAYS, hoursSummary } from "./hours.jsx";
import { CardTraderMark, ModalHost, Dialog, ConfirmDialog, useConfirm } from "./chrome.jsx";
import { LocationPicker, PinView } from "./location.jsx";
import { useCopy } from "./referral.jsx";
import { hrefFor } from "./router.jsx";

const { Button, Icon, IconButton, Badge, Card, Input, Select, Switch, Tag, FluteDivider } = window.CardboardmarketDesignSystem_2a3239;

/**
 * Applying to be a shop, in four steps.
 *
 * A SCREEN RATHER THAN THE DIALOG THIS REPLACES, and the phone is what settled
 * it. Everything a shop's page needs — a line about itself, what it sells,
 * whether you can meet at the counter, how many tables, and a week of opening
 * hours — was put into the one dialog that existed, and on a Pixel the Send
 * button ended up below the bottom of the screen: the browser test could not
 * reach it, and neither could a shopkeeper. Seven rows of hours do not fit in a
 * dialog, which is why the design has steps.
 *
 * FOUR, NOT FIVE. The design's fifth is a team, and staff accounts are cut from
 * this version — "skip for now the step or whole feature of multi-user shop
 * managing" — so the step goes with them rather than standing there empty.
 *
 * NOTHING IS SENT UNTIL THE LAST STEP. Each step validates its own fields on
 * the way out, because being told about a bad phone number four screens later
 * is how a form loses somebody.
 */
const TIMES = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00',
  '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00', '23:00'];
export const DEFAULT_HOURS = {
  mon: { open: '10:00', close: '20:00' }, tue: { open: '10:00', close: '20:00' },
  wed: { open: '10:00', close: '20:00' }, thu: { open: '10:00', close: '20:00' },
  fri: { open: '10:00', close: '20:00' }, sat: { open: '10:00', close: '14:00' },
  sun: 'closed',
};

function WeekHours({ value, onChange }) {
  const { t } = useApp();
  const hours = value || DEFAULT_HOURS;
  const set = (day, next) => onChange({ ...hours, [day]: next });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {DAYS.map(([day, label]) => {
        const v = hours[day];
        const closed = v === 'closed' || !v;
        return (
          <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <span style={{ flex: '0 0 84px', font: 'var(--type-ui)', fontSize: 'var(--text-sm)',
                           color: closed ? 'var(--text-muted)' : 'var(--text-strong)' }}>{t(label)}</span>
            {closed ? (
              <span style={{ flex: 1, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {t("Closed")}
              </span>
            ) : (
              <>
                <Select size="sm" wrapperStyle={{ flex: 1 }} value={v.open} options={TIMES}
                        onChange={(x) => set(day, { ...v, open: x })} />
                <Select size="sm" wrapperStyle={{ flex: 1 }} value={v.close} options={TIMES}
                        onChange={(x) => set(day, { ...v, close: x })} />
              </>
            )}
            {/* The switch rather than a "closed" option among the times: closing
                on Sundays is the commonest thing a shop says about its week and
                should not be hidden at the end of a list of hours. */}
            <Switch checked={!closed} aria-label={t("Open on {day}", { day: t(label) })}
                    onChange={(on) => set(day, on ? { open: '10:00', close: '20:00' } : 'closed')} />
          </div>
        );
      })}
      <div>
        <Button size="sm" variant="ghost" onClick={() => {
          const m = hours.mon;
          onChange({ ...hours, tue: m, wed: m, thu: m, fri: m });
        }}>{t("Copy Monday to the rest of the week")}</Button>
      </div>
    </div>
  );
}

/** How the shop will read to somebody else, beside the form that writes it. */
function Preview({ form }) {
  const { t } = useApp();
  const summary = hoursSummary(form.hours, t);
  return (
    <Card padding="var(--space-5)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ font: 'var(--type-h3)', color: form.name.trim() ? 'var(--text-strong)' : 'var(--text-faint)' }}>
          {form.name.trim() || t("Your store")}
        </span>
        <Icon name="shield-check" size={15} color="var(--status-verified)" />
      </div>
      {form.about.trim() ? (
        <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-body)' }}>{form.about.trim()}</span>
      ) : null}
      {form.games.length ? (
        <div className="cbm-wrap">
          {GAMES.filter((g) => form.games.includes(g.id)).map((g) => <Tag key={g.id}>{g.label}</Tag>)}
        </div>
      ) : null}
      {summary ? <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>{summary}</span> : null}
      {form.counter ? (
        <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>{t("Traders can meet here")}</span>
      ) : null}
    </Card>
  );
}

const STEPS = ["The store", "Where and when", "Contact", "Check it over"];

/**
 * The browser's own IANA zone, or nothing.
 *
 * Wrapped because `resolvedOptions()` is not guaranteed to carry `timeZone` on
 * every engine, and an application that throws here would be an application
 * that cannot be sent — over a field nobody asked for.
 */
function zoneOfThisDevice() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (e) {
    return null;
  }
}

/**
 * Whether saving `form` and `pin` over `shop` changes what an operator
 * checked: the server's own rule in `PUT /api/shop` — the name, the address,
 * or the pin by more than ten metres — so the question is asked exactly when
 * the tick would be taken.
 */
function movesWhatWasChecked(shop, form, pin) {
  if (form.name.trim() !== (shop.name || '')) return true;
  if (form.address.trim() !== (shop.address || '')) return true;
  if (!pin || !shop.point) return false;
  const rad = Math.PI / 180;
  const dLat = (pin[0] - shop.point.lat) * rad;
  const dLng = (pin[1] - shop.point.lng) * rad * Math.cos(shop.point.lat * rad);
  return Math.hypot(dLat, dLng) * 6_371_000 > 10;
}

/**
 * THE APPLICATION, AND THE PLACE A SHOP EDITS ITSELF — one component, two shapes.
 *
 * `editing` is what the back office passes. Reported: "we show a summary of the
 * shop and a button to change details. Clicking it takes us to another summary
 * with another change the details button and when that one is clicked it takes
 * us to the stepped form. We should skip all the middle things and in the same
 * page show all these information and form to change things."
 *
 * Both middle layers were this screen refusing to be an editor. It answered an
 * existing shop with a STATUS card, which is right the first time — a form
 * refilled with what you already sent reads as though nothing was sent — and
 * wrong every time after, because a shop coming here from its own back office
 * has come to change something. The second button then faked a rejection
 * (`setShop({ ...shop, status: 'rejected' })`) to make the form appear at all,
 * which is the shape of a thing that wanted a flag and was given a lie.
 *
 * THE STEPPER GOES WITH IT. Four steps are right for a first application on a
 * phone: it is long, it is unfamiliar, and the hours alone do not fit beside
 * anything. Editing is the opposite — you know the one field you came for, and
 * a wizard makes you walk past three screens to reach it. So editing draws
 * every card at once, with one Save, and the review step disappears because
 * reviewing is what you are already doing.
 */

/*
 * THE COUNTS, AS SENTENCES. The server answers copies and trades
 * (`GET /api/shop/cardtrader/write-back`); the words are made here, one key per
 * shape of number, because "1 copies" is a sentence nobody wrote.
 */
function takeOffSentence(t, c) {
  if (c.copies === 0) return t("No open trade has copies to take off right now.");
  if (c.copies === 1) return t("1 copy from an open trade comes off CardTrader now.");
  if (c.trades === 1) return t("{copies} copies from an open trade come off CardTrader now.", { copies: c.copies });
  return t("{copies} copies from {trades} open trades come off CardTrader now.", { copies: c.copies, trades: c.trades });
}

/*
 * "Off CardTrader right now" is true of every copy `out` counts, a cancelled
 * trade's still waiting to go back included (ticket 07's note): those are off
 * now, and since ticket 15 neither switching off nor disconnecting sends them
 * back — the dialog says they are the shop's to put back.
 */
function outSentence(t, out) {
  if (out.copies === 0) return t("No copies are off CardTrader because of trades here right now.");
  if (out.copies === 1) return t("1 copy is off CardTrader right now because of a trade here.");
  if (out.trades === 1) return t("{copies} copies are off CardTrader right now because of a trade here.", { copies: out.copies });
  return t("{copies} copies are off CardTrader right now because of {trades} trades here.", { copies: out.copies, trades: out.trades });
}

/* Never counted as out — telling a shop to put back copies that may be back is too many. */
function uncertainSentence(t, out) {
  if (!out.uncertain) return null;
  return out.uncertain === 1
    ? t("1 more copy was sent to CardTrader without an answer. We are still checking it.")
    : t("{copies} more copies were sent to CardTrader without an answer. We are still checking them.", { copies: out.uncertain });
}

/* Take-offs owed and not sent: switching off or disconnecting leaves them on sale there. */
function staySentence(t, c) {
  if (!c.copies) return null;
  return c.copies === 1
    ? t("1 copy still waiting to come off stays on sale there.")
    : t("{copies} copies still waiting to come off stay on sale there.", { copies: c.copies });
}

/**
 * THE CONFIRMATION, for switching on, switching off and disconnecting — each
 * saying plainly what will happen and how many copies it concerns.
 */
function WriteBackConfirmation({ kind, standing, busy, onConfirm, onClose }) {
  const { t } = useApp();
  const out = standing.out;
  const lines = kind === 'on'
    ? [takeOffSentence(t, standing.toTakeOff)]
    : kind === 'off'
      /*
       * Switching off hands the shop its CardTrader stock (owner, 2026-10-17):
       * nothing more is written, put-backs included, and not again on
       * switching back on. `out` includes a cancelled trade whose put-back was
       * still owed, which switching off drops — hence "or already has".
       */
      ? [
          outSentence(t, out),
          out.copies ? t("If any of those trades falls through, or already has, putting the copies back on CardTrader is yours to do by hand: we will not do it, even if you switch this on again.") : null,
          uncertainSentence(t, out),
          staySentence(t, standing.toTakeOff),
        ]
      : [
          ...(standing.writesEnabled || out.copies || out.uncertain ? [outSentence(t, out)] : []),
          /* `out` includes a cancelled trade whose put-back is still owed: disconnecting
             drops that put-back too, so the sentence covers trades already fallen through. */
          out.copies ? t("If any of those trades falls through, or already has, putting the copies back on CardTrader is yours to do by hand: we will not be able to.") : null,
          uncertainSentence(t, out),
          standing.writesEnabled ? staySentence(t, standing.toTakeOff) : null,
        ];
  /*
   * Only dev records now (ticket 15), but pre holds writes recorded before it
   * wrote for real, so this counts them rather than blaming the environment.
   */
  if (kind !== 'on' && out.recordedOnly) {
    lines.push(out.recordedOnly >= out.copies
      ? t("None of these were really sent to CardTrader: they were only recorded.")
      : out.recordedOnly === 1
        ? t("1 of these copies was only recorded, never really sent to CardTrader.")
        : t("{copies} of these copies were only recorded, never really sent to CardTrader.", { copies: out.recordedOnly }));
  }

  const words = {
    on: {
      title: t("Update your CardTrader stock?"),
      description: t("From now on, accepting a trade here takes those copies off your CardTrader stock, and cancelling it puts them back. We only ever change the quantity — never a price, and never a card you posted here by hand."),
      confirm: t("Switch on"),
    },
    off: {
      title: t("Stop updating your CardTrader stock?"),
      description: t("Accepting or cancelling a trade here will no longer change your CardTrader stock. From then on, keeping it right is up to you — for trades already open too."),
      confirm: t("Switch off"),
    },
    disconnect: {
      title: t("Disconnect CardTrader?"),
      description: t("Your stock here stops following CardTrader, and we delete your token."),
      confirm: t("Disconnect"),
    },
  }[kind];

  /* The shared confirmation (chrome.jsx), its counts as the body. */
  return (
    <ConfirmDialog role="dialog" width={460}
      title={words.title} description={words.description}
      confirmLabel={words.confirm} busyLabel={t("One moment…")} busy={busy}
      onConfirm={onConfirm} onCancel={onClose}
    >
      <div data-cbm="write-back-counts" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {lines.filter(Boolean).map((line) => (
          <p key={line} style={{ font: 'var(--type-body)', color: 'var(--text-body)', margin: 0 }}>{line}</p>
        ))}
      </div>
    </ConfirmDialog>
  );
}

/**
 * KEEPING THIS SHOP'S STOCK IN STEP WITH WHERE THEY REALLY SELL.
 *
 * Migration 138 and the routes beside it. CardTrader has no OAuth and no
 * delegation of any kind — a token is generated by the seller in their own
 * profile settings and represents one account — so the only shape available is
 * this: the shop pastes theirs. The owner, on being told: "que la tienda tenga
 * que configurar su token en el dashboard de tienda no es nada malo."
 *
 * SO THE SCREEN HAS TO SAY WHAT THE TOKEN CAN DO. The same credential reaches
 * their products — creating, editing and deleting — and CardTrader offers no
 * read-only scope. A shop handing that over deserves to read it in a sentence
 * before they paste, not to find it in a changelog. The owner asked for exactly
 * that: "siempre con toda esta informacion bien explicada para ellos."
 *
 * WRITING IS A SECOND QUESTION, ASKED LATER. Connecting is reading, and reading
 * touches nothing of theirs. The switch below only appears once there is a
 * connection to attach it to.
 *
 * ITS OWN COMPONENT because it owns a credential: nothing above it should ever
 * have the token in scope, and the state here is cleared the moment it is sent.
 */
function SyncPanel() {
  const { locale, notify, t } = useApp();
  const copy = useCopy();
  const [state, setState] = React.useState(undefined);  // undefined = still asking
  const [token, setToken] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  /*
   * Where write-back stands — counts only — asked with the connection so the
   * line under the switch is there on the first frame rather than arriving
   * under it. Null when there is no connection or the answer did not come.
   */
  const [standing, setStanding] = React.useState(null);
  /* The confirmation open, if any: which act, and the counts it was opened with. */
  const [confirming, setConfirming] = React.useState(null);

  const load = React.useCallback(() => {
    api.cardtrader()
      .then(async (r) => {
        const s = r && r.connection ? await api.cardtraderWriteBack().catch(() => null) : null;
        setStanding(s);
        setState(r);
      })
      .catch(() => setState({ storable: false, connection: null, unreachable: true }));
  }, []);
  React.useEffect(load, [load]);

  if (state === undefined) return null;

  const connection = state.connection;

  /*
   * EVERY ONE OF THE THREE ACTS IS CONFIRMED, WITH ITS COUNT, asked fresh.
   * The counts on screen may be minutes old, and the number a shop agrees to
   * is the one it should get: switching on takes those copies off now, and
   * disconnecting leaves the copies that are out for the shop to put back by
   * hand (spec, stories 4–8).
   */
  const ask = async (kind) => {
    if (busy) return;
    setBusy(true);
    try {
      setConfirming({ kind, standing: await api.cardtraderWriteBack() });
    } catch (e) {
      /* No connection any more (another tab disconnected it): show the truth. */
      if (e && e.code === 'not_connected') load();
      else notify((e && e.message) || t("That did not save."), 'caution');
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    const kind = confirming && confirming.kind;
    if (!kind || busy) return;
    setBusy(true);
    try {
      if (kind === 'disconnect') {
        await api.disconnectCardtrader();
        notify(t("CardTrader disconnected."));
      } else {
        await api.setCardtraderWrites(kind === 'on');
        notify(kind === 'on'
          ? t("Done. Accepting a trade here now takes those copies off CardTrader.")
          : t("Done. Accepting a trade here no longer changes CardTrader."));
      }
      setConfirming(null);
      load();
    } catch (e) {
      notify((e && e.message) || t("That did not save."), 'caution');
    } finally { setBusy(false); }
  };

  const connect = async () => {
    const pasted = token.trim();
    if (!pasted || busy) return;
    setBusy(true);
    try {
      await api.connectCardtrader(pasted);
      /*
       * CLEARED THE MOMENT IT IS SENT, successfully or not. A credential has no
       * business sitting in a React state after the request that needed it, and
       * a field still holding it invites a second paste on top of the first.
       */
      setToken('');
      notify(t("CardTrader connected."));
      load();
    } catch (e) {
      setToken('');
      notify(
        e && e.code === 'token_refused'
          ? t("CardTrader did not accept that token. Check you copied all of it from their settings page.")
        : e && e.code === 'cardtrader_unreachable'
          ? t("We could not reach CardTrader just now. Your token was not stored — try again in a minute.")
        /* Them refusing US for a while, not the token: wait, do not regenerate. */
        : e && e.code === 'upstream_unavailable'
          ? t("CardTrader is not answering right now. Your token was not stored — try again in a few minutes.")
        : e && e.code === 'cannot_store_secrets'
          ? t("We cannot store a credential safely right now, so we did not take it.")
          : (e && e.message) || t("That did not save."),
        'caution',
      );
    } finally { setBusy(false); }
  };

  return (
    <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}
          data-cbm="sync-panel">
      {/*
        THE SECTION IS THE JOB, NOT THE CREDENTIAL.
        The owner: "the whole section this sits in should be a 'Synchronization'
        section, where we happen to support only CardTrader for now. Not a cold
        'Paste your CardTrader token here'."
        Which is right twice over. A shop reading "Your CardTrader account" has
        to work out what we would do with it; a shop reading "Synchronisation"
        already knows, and CardTrader is then an answer to "how", not a demand.
        It also stops the screen having to be rebuilt the day a second
        marketplace is possible — the frame is already the general one.
        NO PROMISE OF MORE, THOUGH. "Right now, CardTrader" is true; "more
        coming" would be a roadmap on a settings page, and Cardmarket offers no
        API that could keep it.
      */}
      <span className="cbm-label">{t("Synchronisation")}</span>
      <span style={{ font: 'var(--type-body)', color: 'var(--text-body)' }}>
        {t("If you sell the same cards somewhere else, we can keep your stock here in step with it — so a card you sold there stops showing here.")}
      </span>
      <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
        {t("Right now we can do this with CardTrader.")}
      </span>
      <FluteDivider />

      {!state.storable ? (
        /* Said instead of the form, not beside it. A field somebody can type a
           credential into, which then refuses it, is worse than no field. */
        <span style={{ font: 'var(--type-body)', color: 'var(--text-body)' }}>
          {t("Connecting a CardTrader account is switched off right now. Nothing you paste here could be stored safely, so we are not asking for it.")}
        </span>
      ) : connection ? (
        <>
          {/*
            THE MARK HEADS THE WHOLE CARDTRADER BLOCK, rather than sitting in a
            row of status.
            Reported with a screenshot: "the cardtrader logo should be at the
            top of the whole configuration settings for CardTrader", and "let's
            give some more space to things". It was crammed between two rules
            with the badge and the token on one line, which read as a status
            strip rather than as the heading of everything under it — and a
            reader had to work out that the badge, the token, the user and the
            webhook below all belonged to the same marketplace.
            On its own line it says "everything from here down is CardTrader",
            which is also the shape that survives a second marketplace.
          */}
          <div style={{ marginTop: 'var(--space-2)' }}>
            <CardTraderMark height={22} />
          </div>

          {/* The facts about this connection, stacked rather than strung along
              one line: they answer different questions and were wrapping into
              each other on a narrow panel. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <Badge tone="positive">{t("Connected")}</Badge>
              <span style={{ font: 'var(--type-body)', color: 'var(--text-body)' }}>
                {t("Token {tail}", { tail: connection.tokenTail })}
              </span>
            </div>
            {/* WHICH ACCOUNT, because a token that authenticates is not
                necessarily the one they meant — somebody with two CardTrader
                accounts can paste the wrong one, and the first sweep is a late
                and confusing place to find that out. */}
            {connection.cardtraderUserId ? (
              <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {t("CardTrader user {id}", { id: connection.cardtraderUserId })}
              </span>
            ) : null}
            {/* WHEN WE LAST LOOKED, which is the question a shop watching their
                stock actually has, and which nothing on this screen answered. */}
            {connection.lastSweptAt ? (
              <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {t("Last checked {when}", { when: new Date(connection.lastSweptAt).toLocaleString(locale) })}
              </span>
            ) : null}
          </div>

          {connection.lastSweepError ? (
            <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--orange-700)' }}>
              {/* The sweep stores only a CODE — the two refusals it can tell
                  apart, and `sweep_failed` for anything of ours — and the
                  sentence is made here, in the shop's language. Never the raw
                  value: a database error never reaches a client. */}
              {connection.lastSweepError === 'token_refused'
                ? t("CardTrader no longer accepts the token this shop connected with. Connect it again with a fresh one.")
                : connection.lastSweepError === 'upstream_unavailable'
                  ? t("CardTrader was not answering when we last checked. The next check will try again.")
                  : t("The last check of your CardTrader stock failed at our end. The next check will try again.")}
            </span>
          ) : null}

          {/*
            THE WRITE-BACK SWITCH (ADR 0011). Asked for as its own decision —
            "la parte de escribir para actualizar su inventario podriamos
            separarla en otra opcion que puede activar o desactivar la tienda"
            — and drawn now that something honours it. Off until the shop puts
            it on (story 3). The switch shows what is STORED, never what was
            just pressed: pressing opens the confirmation, and only a confirmed
            answer moves it.
          */}
          <FluteDivider />
          <div data-cbm="write-back" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-base)', color: 'var(--text-strong)' }}>
                  {t("Update my CardTrader stock")}
                </div>
                <div style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {t("When you accept a trade here, we take those copies off your CardTrader stock, and put them back if the trade is cancelled. Only the quantity, never a price.")}
                </div>
              </div>
              <Switch checked={!!connection.writesEnabled} disabled={busy}
                      aria-label={t("Update my CardTrader stock")}
                      onChange={() => ask(connection.writesEnabled ? 'off' : 'on')} />
            </div>
            {connection.writesEnabled && standing && standing.out.copies > 0 ? (
              <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-body)' }}>
                {outSentence(t, standing.out)}
              </span>
            ) : null}
            <a
              className="cbm-textlink"
              href={`https://cardboardmarket.xyz${locale === 'en-US' ? '/cardtrader-stock-updates' : '/actualizar-stock-cardtrader'}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)' }}
            >
              {t("How updating your CardTrader stock works")}
            </a>
          </div>

          {/*
            INSTANT NOTICE OF A SALE — OPTIONAL, AND SET UP BY THEM.
            The owner, twice. First on why it is optional: the five-minute
            sweep already sees everything this would, sales included, so this
            only moves the moment from minutes to seconds and a shop that skips
            it is correct, merely slower. Then on who does it: "No cambiemos
            nosotros el webhook a través de la API. Demos instrucciones de como
            deben hacerlo y el link que deben poner los propios owners de la
            tienda."
            Which is better than it sounds. CardTrader allows one notification
            address per account and will not let us read it, so setting it
            ourselves meant writing over something we could not see. Handing
            over the address means their token is only ever used to READ their
            stock — the promise the connection makes two paragraphs up.
          */}
          <FluteDivider />
          <span className="cbm-label">{t("Instant notice of a sale")}</span>
          <span style={{ font: 'var(--type-body)', color: 'var(--text-body)' }}>
            {/*
              THE DIRECTION, SPELLED OUT AT BOTH ENDS.
              This read "a sale here stops showing in seconds", which the owner
              caught: "makes it seem like if they sell some card in our platform
              we take it out of CardTrader in seconds." It is the other way
              round, and it has to be, because a webhook only tells us a thing
              happened — nothing CardTrader sends lets us change their side.
              So both the where-it-sold and the where-it-disappears are named.
            */}
            {t("Optional. Without it we check your CardTrader stock every five minutes, which already catches everything — sales included. With it, a card you sell on CardTrader stops showing here within seconds.")}
          </span>

          {connection.webhookUrl ? (
            <>
              {/*
                THE EVIDENCE, AND ITS ABSENCE, SAID HONESTLY. We cannot read
                their settings, so "connected" is not a claim we are entitled
                to make — only "something arrived, at this time" is.
              */}
              {connection.webhookLastDeliveryAt ? (
                <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--green-700, var(--text-muted))' }}>
                  {t("Working — the last notice arrived {when}.", {
                    when: new Date(connection.webhookLastDeliveryAt).toLocaleString(locale),
                  })}
                </span>
              ) : (
                <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {t("Nothing has arrived yet. It only fires when something sells, so this may simply mean nothing has.")}
                </span>
              )}

              {/*
                THE STEPS LIVE ON A PAGE, NOT IN THIS PANEL.
                The owner, having read the cramped version: "Instead of trying
                to fit all that information in the setting section of the shop,
                let's create a blog-post-like tutorial and link them there."
                Right — it is four steps in somebody else's interface, plus
                what to know before, plus how to tell it worked, plus how to
                undo it. Squeezed in here it was a wall nobody reads beside the
                one thing they came for, which is the address.
                So the panel keeps the address and the copy button, and the
                page carries the rest. The link opens in a new tab: somebody
                following instructions should not lose the address they are
                copying from.
              */}
              <a
                className="cbm-textlink"
                href={`https://cardboardmarket.xyz${locale === 'en-US' ? '/cardtrader-sale-sync' : '/sincronizacion-ventas-cardtrader'}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)' }}
              >
                {t("How to set this up in CardTrader, step by step")}
              </a>

              {/*
                THE ADDRESS, AND AN ICON TO COPY IT.
                Reported with a screenshot: "The copy button is too big and
                doesn't fit." It did not — a worded button beside a field
                holding a sixty-character url left the url about forty pixels
                wide on a phone, which is the one thing on this row that has to
                be readable. `label` still carries the word, so a screen reader
                and the test both get "Copy": an icon button with no name is a
                button nobody can describe.
                READ-ONLY rather than disabled — an editable field invites an
                edit and an edited address is deliveries going nowhere — but
                still selectable, because the clipboard can refuse.
              */}
              {/*
                THE HINT IS OUTSIDE THE ROW, which is what finally lines the
                button up.
                Reported twice. `align-items: flex-end` aligns to the bottom of
                each CHILD, and the Input's child is not the box — it is the
                whole control, label and box and hint stacked. So the button sat
                level with the last line of the hint, two rows below the box it
                belongs to, and no amount of nudging the button would fix a
                thing about the button.
                With the hint lifted out, the Input is label + box, its bottom
                IS the box's bottom, and flex-end means what it looks like it
                means. The hint then reads across the full width instead of
                wrapping into a narrow column beside the button, which it was
                also doing.
              */}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-3)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Input
                    label={t("Your address")}
                    value={connection.webhookUrl}
                    readOnly
                    onChange={() => {}}
                  />
                </div>
                <IconButton
                  icon="copy"
                  variant="outline"
                  label={t("Copy")}
                  onClick={() => copy(connection.webhookUrl, t("Address copied."))}
                />
              </div>
              {/* "Keep it a secret" rather than "only yours": the first says
                  what to DO. */}
              <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {t("Keep it a secret. Anyone with it could tell us your stock changed.")}
              </span>

              {/*
                NO "STOP USING IT" BUTTON, which is the owner's correction:
                "what's the 'Stop using it' button for? It clears the webhook?
                If that's the case, remove it. We don't touch their webhook.
                They add it or remove it whenever they want, but themselves."
                It cleared OUR slug rather than their setting — and it should
                still go. It offered a HALF-stop: CardTrader would carry on
                posting to an address we had quietly stopped listening on, with
                nothing on either screen saying so. Removing it in their own
                settings is the whole of stopping, in one place rather than
                two, and the guide says how. Disconnecting the account here
                still takes the slug with it.
                The one-address warning left too — it lives in the guide, where
                somebody reads it BEFORE they start.
              */}
            </>
          ) : (
            <div>
              <Button variant="outline" size="sm" disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        try { await api.cardtraderWebhookUrl(); load(); }
                        catch (e) { notify((e && e.message) || t("That did not save."), 'caution'); }
                        finally { setBusy(false); }
                      }}>
                {t("Show me how")}
              </Button>
            </div>
          )}

          <FluteDivider />
          <div>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => ask('disconnect')}>
              {t("Disconnect")}
            </Button>
          </div>

          {/* No ModalHost here: ConfirmDialog brings its own, and an outer one
              would lay an empty full-screen layer over it. */}
          {confirming ? (
            <WriteBackConfirmation kind={confirming.kind} standing={confirming.standing} busy={busy}
                                   onConfirm={confirm} onClose={() => setConfirming(null)} />
          ) : null}
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <CardTraderMark height={18} />
            <span style={{ font: 'var(--type-body)', color: 'var(--text-body)' }}>
              {t("Paste the API token from your profile settings there, and we take it from there.")}
            </span>
          </div>
          {/*
            SAID BEFORE THEY PASTE, not after. CardTrader issues one kind of
            token and it can change their products as well as read them — there
            is no read-only option to offer instead. Somebody handing that over
            should read it in a sentence rather than find it later.
          */}
          <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
            {t("CardTrader only issues one kind of token, and it can change your listings there as well as read them. We encrypt it, we only read with it unless you switch the setting below on, and you can disconnect whenever you like.")}
          </span>
          <Input
            label={t("CardTrader API token")}
            value={token}
            type="password"
            autoComplete="off"
            onChange={(e) => setToken(e.target.value)}
            hint={t("CardTrader profile → settings → API. We never show it back to you.")}
          />
          <div>
            <Button disabled={busy || !token.trim()} onClick={connect}>
              {busy ? t("One moment…") : t("Connect")}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

/*
 * WHERE THE COUNTER IS, chosen on a map and handed back to the form.
 *
 * Not the location dialog: that one saves the account's own point, and a
 * session acting as a shop is refused those routes — a shop's one location is
 * its counter, which only the application's save writes. This one saves
 * nothing; the pin travels with "Save changes" or "Send".
 */
function PinDialog({ value, onSave, onClose }) {
  const { t } = useApp();
  const [p, setP] = React.useState(value);
  return (
    <Dialog open role="dialog" width={420} closeLabel={t("Close")} onClose={onClose}
      aria-label={t("Where the door is")} title={t("Where the door is")}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
        {/* Nothing to save until the pin is somewhere it was not. */}
        <Button onClick={() => onSave(p)} disabled={!p || (value && p.join(',') === value.join(','))}>{t("Save")}</Button>
      </>}
    >
      <LocationPicker value={p} onChange={setP} />
    </Dialog>
  );
}

export function ShopScreen({ editing = false } = {}) {
  const { point, go, notify, t } = useApp();
  /*
   * THE COUNTER'S PIN, held by this form and sent with it — never the
   * account's own point.
   *
   * It was `point`, the account's home point, moved through the location
   * routes and copied into the application on save. The owner, 2026-10-17: a
   * shop has one location, its counter, so a session acting as a shop is
   * refused those routes (`shop_moves_with_its_counter`) and the pin can only
   * travel in `PUT /api/shop`, which writes the counter and the account point
   * together. A shop's pin starts at its counter; an applicant's at where
   * they are set, because applying is done standing in the store.
   */
  const [pin, setPin] = React.useState(point);
  React.useEffect(() => { setPin((was) => was || point); }, [point && point.join(',')]);
  const [shop, setShop] = React.useState(undefined);
  const [step, setStep] = React.useState(1);
  const [tried, setTried] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [closing, setClosing] = React.useState(false);
  const [confirm, confirmDialog] = useConfirm();
  /*
   * SET YOUR POINT WITHOUT LEAVING THE FORM YOU ARE FILLING IN.
   *
   * This button used to navigate to the Nearby screen, which is gone — the
   * owner retired it because browse already answers what it answered, with
   * filters and paging a map cannot. Sending a shop applicant there to drop
   * a pin was always the long way round: the pin belongs to the LOCATION
   * DIALOG, which Nearby merely happened to sit beside.
   *
   * So it opens here, over the half-filled application, exactly as browse
   * opens it. Leaving the form to go and set a point is how a half-filled
   * form gets abandoned, and the rule for applying is that you are STANDING
   * IN THE SHOP — so the moment they are asked is the moment they can answer.
   */
  const [locating, setLocating] = React.useState(false);
  const [finding, setFinding] = React.useState(false);

  /*
   * THE ADDRESS, TURNED INTO THE PIN.
   *
   * The owner: "a shop is very static usually, so the address should be their
   * point." A shopkeeper who has typed where the door is should not then have
   * to find the same door on a map.
   *
   * Biased towards the pin they already have, where there is one, so an
   * ambiguous street name resolves to the branch they are standing in rather
   * than the one in another province. `/api/places` takes that as lat/lng and
   * builds a view box around it.
   *
   * IT SETS THE FORM'S PIN, which the save carries to the counter — see `pin`.
   *
   * REFUSES QUIETLY RATHER THAN GUESSING. An address a geocoder has never heard
   * of is an ordinary outcome, not an error anybody can act on, and dropping a
   * pin on its best guess would put a shop somewhere it is not.
   */
  const findTheAddress = async () => {
    setFinding(true);
    try {
      const found = await api.searchPlaces({
        q: form.address.trim(),
        ...(pin ? { lat: pin[0], lng: pin[1] } : {}),
        limit: 1,
      });
      const best = (found.places || found || [])[0];
      if (!best || typeof best.lat !== 'number') {
        notify(t("We could not find that address. Drop the pin instead."), 'caution');
        return;
      }
      setPin([best.lat, best.lng]);
      notify(t("The pin is on your address now."));
    } catch {
      notify(t("We could not find that address. Drop the pin instead."), 'caution');
    } finally {
      setFinding(false);
    }
  };
  const [form, setForm] = React.useState({
    name: '', about: '', games: [], counter: true, primaryStock: '',
    hours: DEFAULT_HOURS, address: '', phone: '', website: '',
    whatsapp: '', whatsappVisible: false, telegram: '', telegramVisible: false,
    cardmarketUsername: '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const allGames = GAMES.every((g) => form.games.includes(g.id));

  /*
   * THE PAGE ADDRESS THE NAME WILL GET, shown before anything is saved. The
   * owner, 2026-10-19: a taken handle gets a free one with a suffix, "but giving
   * feedback to the user previously for him to know before accepting anything".
   *
   * Asked of the server, because only it knows which handles are taken — and
   * a quarter of a second after the typing stops, so a name typed at speed is
   * one question rather than one per letter. A stale answer is dropped by the
   * `live` flag rather than raced against the newer one.
   *
   * KEPT WITH THE NAME IT ANSWERS, and shown only while that is still the name
   * in the box: an address for what was typed a moment ago is not the address
   * this name gets, and sending it back as the promise would report a move
   * that never happened.
   */
  const [forecast, setForecast] = React.useState(null);
  React.useEffect(() => {
    const name = form.name.trim();
    if (name.length < 2) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      api.shopHandle(name)
        .then((r) => { if (live) setForecast({ name, handle: r.handle || null }); })
        .catch(() => { if (live) setForecast(null); });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [form.name]);
  const handle = forecast && forecast.name === form.name.trim() ? forecast.handle : null;
  const pageAddress = (h) => `${window.location.host}/trader/${h}`;
  // The handle the save gave instead of the one shown, when a race took that one.
  const [movedTo, setMovedTo] = React.useState(null);
  const movedSentence = (h) => t("Another store took that address a moment before you. Your store’s page is {address}.", { address: pageAddress(h) });
  const movedLine = movedTo ? (
    <p data-testid="shop-page-moved" style={{ font: 'var(--type-body-sm)', color: 'var(--text-strong)', margin: 0, overflowWrap: 'anywhere' }}>
      {movedSentence(movedTo)}
    </p>
  ) : null;

  React.useEffect(() => {
    let live = true;
    api.myShop()
      .then((r) => {
        if (!live) return;
        setShop(r.shop);
        if (r.shop) {
          setForm({
            name: r.shop.name || '', about: r.shop.about || '',
            games: r.shop.games || [], counter: r.shop.counter !== false,
            primaryStock: r.shop.primaryStock || '',
            hours: r.shop.hours || DEFAULT_HOURS,
            address: r.shop.address || '', phone: r.shop.phone || '',
            website: r.shop.website || '',
            whatsapp: r.shop.whatsapp || '', whatsappVisible: !!r.shop.whatsappVisible,
            telegram: r.shop.telegram || '', telegramVisible: !!r.shop.telegramVisible,
            cardmarketUsername: r.shop.cardmarketUsername || '',
          });
          // The counter, which is where a shop is.
          if (r.shop.point) setPin([r.shop.point.lat, r.shop.point.lng]);
        }
      })
      .catch(() => { if (live) setShop(null); });
    return () => { live = false; };
  }, []);

  /*
   * Checked on the way out of each step rather than all at the end. A form that
   * only tells you the rule after four more screens is a form that made you
   * break it.
   */
  const errors = {
    1: {
      name: !form.name.trim() ? t("Give the name on the sign.") : null,
      // A shop that sells nothing is not an answer. "All games" is one tap.
      games: !form.games.length ? t("Pick at least one game, or all of them.") : null,
    },
    2: {
      point: !pin ? t("Set where you are first — that is where the store goes on the map.") : null,
      hours: DAYS.every(([d]) => form.hours[d] === 'closed')
        ? t("Open at least one day.")
        : DAYS.some(([d, label]) => form.hours[d] !== 'closed' && form.hours[d].close <= form.hours[d].open)
          ? t("One day closes before it opens.") : null,
    },
    3: { address: form.address.trim().length < 6 ? t("Give the street address of the store.") : null },
    4: {},
  };
  /*
   * WHEN EVERY CARD IS ON SCREEN, EVERY CARD IS VALIDATED.
   *
   * `errors` is keyed by step because the wizard only ever shows one. Editing
   * shows them all, so a message belonging to step 2 has to be reachable while
   * the thing it is about is visible — otherwise saving with no address does
   * nothing and says nothing, which is the worst of both.
   */
  const visible = editing ? Object.assign({}, errors[1], errors[2], errors[3]) : errors[step];
  const err = (k) => (tried ? visible[k] : null);
  const ok = !Object.values(visible).some(Boolean);

  const send = async () => {
    /*
     * A VERIFIED STORE MOVING WHAT WAS CHECKED IS ASKED FIRST (the owner,
     * 2026-10-23: "confirmation should be added anywhere we're changing
     * something kind of important"). The server sends a store back to be
     * checked when its name, its address or its pin (over ten metres) changes,
     * and only an operator gives the tick back: one Save would otherwise take
     * a live store's badge and page down until somebody goes round again.
     */
    if (editing && shop && shop.status === 'verified' && movesWhatWasChecked(shop, form, pin)) {
      if (!(await confirm({
        title: t("Send your store to be checked again?"),
        description: t("Changing the name, the address or the pin sends the store back to be checked. Until it is, it is no longer a verified store and its page is not shown."),
        confirmLabel: t("Save and send to be checked"),
      }))) return;
    }
    const zone = zoneOfThisDevice();
    // What the form promised, and what the shop had, as they were when Send was pressed.
    const promised = handle;
    const before = shop && shop.handle ? shop.handle : null;
    setBusy(true);
    try {
      const r = await api.applyShop({
        name: form.name.trim(), address: form.address.trim(),
        // The address this form promised, so the answer can say if it moved.
        ...(promised ? { shownHandle: promised } : {}),
        lat: pin[0], lng: pin[1],
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
        ...(form.website.trim() ? { website: form.website.trim() } : {}),
        /*
         * Sent as empty strings where they were cleared, so "I took my number
         * down" reaches the server as an instruction rather than as silence —
         * an omitted field means "leave it", which is the opposite.
         */
        whatsapp: form.whatsapp.trim(), whatsappVisible: form.whatsappVisible,
        telegram: form.telegram.trim(), telegramVisible: form.telegramVisible,
        /*
         * ALWAYS SENT, including empty.
         *
         * This used to be omitted when blank, which quietly made the field
         * one-way: the route treats an absent key as "leave it alone", so a
         * shop that deleted its Cardmarket name and saved got the old one back
         * with no sign anything had been ignored. Empty is a real answer here
         * and it has to travel to be heard.
         */
        cardmarketUsername: form.cardmarketUsername.trim(),
        ...(form.about.trim() ? { about: form.about.trim() } : {}),
        games: form.games, counter: form.counter, hours: form.hours,
        /*
         * Always sent, empty included: "" is how a shop says its stock came back
         * here, and an omitted field would be read as "leave it" — the one-way
         * door the Cardmarket name had.
         */
        primaryStock: form.primaryStock,
        /*
         * WHICH CLOCK THOSE HOURS ARE KEPT ON, taken from the browser rather
         * than asked for.
         *
         * "10:00" on a shop's page is read by a human who knows which ten
         * o'clock is meant; a rule that refuses a meetup outside opening hours
         * does not, and a meetup is an instant. The browser typing this is
         * standing in the shop — that is the rule for the pin — so its zone is
         * the shop's. See migration 130.
         *
         * Guarded because a runtime without it must send nothing rather than
         * `undefined`: the server keeps the clock it already had.
         */
        ...(zone ? { timezone: zone } : {}),
      });
      setShop(r.shop);
      setStep(1);
      /*
       * SOMEBODY TOOK THE SHOWN ADDRESS IN BETWEEN: the save took the next free
       * one rather than refusing, and says which — in the toast, and on the
       * screen that follows (the state screen, or this form when editing),
       * because a toast is gone before it is read twice.
       *
       * AND WHEN NOTHING WAS SHOWN — saved inside the debounce, or the forecast
       * failed — a new address is still said, as a plain fact: nobody was
       * promised anything, but nobody should find out from a broken link.
       */
      const saved = r.shop && r.shop.handle ? r.shop.handle : null;
      const moved = r.handleMoved && saved ? saved : null;
      setMovedTo(moved);
      if (moved) {
        notify(movedSentence(moved), 'caution');
      } else if (!promised && saved && saved !== before) {
        notify(t("Your store’s page is {address}.", { address: pageAddress(saved) }));
      } else {
        notify(t("Sent. We check the address and tell you."));
      }
    } catch (e) {
      /*
       * By CODE, never by message: `e.message` is already a translated sentence.
       * The phone gets its own two, because a business number missing its
       * country code is a complete thought missing one piece.
       */
      notify(
        e && e.code === 'shop_phone_needs_country' ? t("That phone needs its country code, like +34.")
          : e && e.code === 'shop_phone_invalid' ? t("That does not look like a phone number.")
          : e.message,
        'caution',
      );
      setStep(3);
    } finally { setBusy(false); }
  };

  /*
   * The application, taken back. The server answers 204 and the screen falls
   * back to the empty form rather than reloading: what somebody does next is
   * either nothing or apply again, and both of those are this screen.
   */
  const withdraw = async () => {
    /* Asked first: the application is deleted, and applying again starts the form over. */
    if (!(await confirm({
      title: t("Withdraw your application?"),
      description: t("What you sent is deleted and nobody checks it. Applying again means filling the form again."),
      confirmLabel: t("Withdraw the application"),
    }))) return;
    setClosing(true);
    try {
      await api.closeShop();
      setShop(null);
      setStep(1);
      notify(t("The application is withdrawn."));
    } catch (e) {
      notify(e.message, 'caution');
    } finally { setClosing(false); }
  };

  const next = () => {
    setTried(true);
    if (!ok) return;
    setTried(false);
    if (editing || step === 4) send(); else { setStep(step + 1); window.scrollTo(0, 0); }
  };

  if (shop === undefined) return null;

  /*
   * ALREADY APPLIED: the state, not the form. Coming back to this screen while
   * an application is being checked should say where it is up to — a form
   * refilled with what you already sent reads as though nothing was sent.
   */
  if (shop && shop.status !== 'rejected' && !editing) {
    return (
      <div className="cbm-stack" style={{ maxWidth: 560 }}>
        {confirmDialog}
        <h1 className="cbm-h1">{shop.status === 'verified' ? t("{name} is live", { name: shop.name }) : t("Your store is being checked")}</h1>
        <p style={{ font: 'var(--type-body)', color: 'var(--text-muted)', margin: 0 }}>
          {shop.status === 'verified'
            ? t("Traders nearby can see it now, and can be offered it as a place to meet.")
            : t("It usually takes less than 48 hours.")}
        </p>
        {movedLine}
        <Preview form={form} />
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          {shop.status === 'verified' && shop.handle ? (
            <Button onClick={() => go('trader', { id: shop.handle })}>{t("See your page")}</Button>
          ) : null}
          <Button variant="outline" onClick={() => setShop({ ...shop, status: 'rejected' })}>
            {t("Change the details")}
          </Button>
          {/*
            TAKING IT BACK, and only while it is still waiting.
            `DELETE /api/shop` has existed since the first version of this
            feature and nothing has ever called it — a right nobody can
            exercise, which is the same gap the data-export button was built to
            close. Somebody who applied by mistake, or typed the wrong shop,
            should not have to ask us.

            NOT OFFERED ONCE IT IS LIVE, deliberately. The route deletes the
            shop PROFILE and leaves the account — with its stock, its trades and
            its conversations — exactly where they were, which is right, and
            means a live shop closing itself would leave its cards in an account
            no screen can reach any more. What should happen to a closed shop's
            stock is a decision, not a button, so it stays unoffered until it is
            made.
          */}
          {shop.status === 'waiting' ? (
            <Button variant="ghost" disabled={closing} onClick={withdraw}>
              {closing ? t("Withdrawing…") : t("Withdraw the application")}
            </Button>
          ) : null}
        </div>
        {shop.status === 'verified' ? (
          <p style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', margin: 0 }}>
            {t("Changing the name or the address sends it back to be checked.")}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="cbm-detail cbm-detail--form">
      {confirmDialog}
      <div className="cbm-stack">
        {/*
          THE INVITATION IS FOR SOMEBODY WHO HAS NOT APPLIED.
          A shop editing its own details from the back office arrived under a
          heading that already says "The store", and telling it to "Open a store
          account" reads as though its application had been lost. The rejected
          note below stays either way: it is the one thing an existing shop may
          still need to read here.
        */}
        {editing ? null : (
          <>
            <h1 className="cbm-h1">{t("Open a store account")}</h1>
            <p style={{ font: 'var(--type-body)', color: 'var(--text-muted)', margin: 0 }}>
              {t("For card shops with a counter. Free, usually within two working days.")}
            </p>
          </>
        )}
        {shop && shop.rejectedNote ? (
          <Card tone="sunken" padding="var(--space-5)">
            <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
              {t("We could not confirm this")}
            </div>
            <div style={{ font: 'var(--type-body-sm)', color: 'var(--text-body)', marginTop: 4 }}>{shop.rejectedNote}</div>
          </Card>
        ) : null}

        {editing ? null : (
        <div className="cbm-steps">
          {STEPS.map((s, i) => (
            <span key={s} data-on={step === i + 1 ? '1' : step > i + 1 ? 'done' : '0'}>
              {String(i + 1).padStart(2, '0')} {t(s)}
            </span>
          ))}
        </div>
        )}

        {editing || step === 1 ? (
          <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <Input label={t("Store name")} value={form.name} error={err('name')}
                   hint={err('name') ? undefined : t("As it reads on the sign. Traders look for it in the street.")}
                   onChange={(e) => set('name', e.target.value)} />
            {/*
              READ-ONLY, AND ALWAYS THERE. The line is drawn before there is an
              address to put in it, so the form does not move down a line the
              moment the first answer arrives.
            */}
            <div data-testid="shop-page-address" style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 'calc(-1 * var(--space-3))' }}>
              <span className="cbm-label">{t("Your store’s page")}</span>
              <span style={{ font: 'var(--type-body-sm)', color: handle ? 'var(--text-strong)' : 'var(--text-faint)', overflowWrap: 'anywhere' }}>
                {/*
                  A LINK ONCE THE PAGE EXISTS. The owner, 2026-10-19: "'Your
                  store's page' should be a link maybe". It exists when the shop
                  is saved under the address shown AND verified: the public page
                  shows a shop only once it has been checked (an application
                  waiting is "No trader by that name"). A forecast for a name
                  not saved yet points at no page either, so both stay text.
                  Same tab, through the app's own route.
                */}
                {handle && shop && shop.status === 'verified' && shop.handle === handle ? (
                  <a href={hrefFor({ name: 'trader', id: handle })}
                     onClick={(e) => {
                       // A modified click is the browser's: a new tab or window, as any link.
                       if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                       e.preventDefault(); go('trader', { id: handle });
                     }}
                     style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                    {pageAddress(handle)}
                  </a>
                ) : handle ? pageAddress(handle) : t("Appears as you type the name.")}
              </span>
              {/* Editing stays on this form, so a moved address is said here. */}
              {editing ? movedLine : null}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span className="cbm-label">{t("What you sell")}</span>
              {/*
                "ALL GAMES" IS A CHIP, not seven taps. Reported on sight: a shop
                that sells everything is the ordinary case for a shop, and
                asking it to tick the whole list one at a time is asking it to
                do our arithmetic.
                
                Tapping one game while All is lit NARROWS to that game rather
                than removing it from six — which is what somebody means when
                they go from "everything" to pointing at one thing. The same
                shape the browse and onboarding pickers use.
              */}
              <div className="cbm-wrap">
                <Tag selected={allGames} onClick={() => set('games', allGames ? [] : GAMES.map((g) => g.id))}>
                  {t("All games")}
                </Tag>
                {GAMES.map((g) => (
                  <Tag key={g.id} selected={!allGames && form.games.includes(g.id)}
                       onClick={() => set('games', allGames ? [g.id]
                         : form.games.includes(g.id) ? form.games.filter((x) => x !== g.id)
                         : [...form.games, g.id])}>
                    {g.label}
                  </Tag>
                ))}
              </div>
              {err('games') ? (
                <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--status-danger)' }}>{err('games')}</span>
              ) : null}
            </div>
            {/*
              WHERE THE REAL STOCK LIVES, asked once and shown on every listing.
              The owner: "a setting a shop can enable or disable to say here
              they have their primary stock with the option of Cardmarket and
              card trader... if set, listings of that shop should show a warning
              saying their stock is primarily handled somewhere else and that
              means the listing in our platform may be outdated or cancelled in
              case of duplicated selling."

              A SELECT RATHER THAN TWO SWITCHES, which is what "exclusive"
              means once it is written down: two switches can both be on and
              then every reader has to invent a rule for it. "Here" is the first
              option and the empty one, so a shop that has not thought about this
              has not accidentally claimed anything.
            */}
            <Select label={t("Where your stock really lives")}
                    value={form.primaryStock}
                    onChange={(v) => set('primaryStock', v)}
                    options={[
                      // "Here" alone: the row is already labelled "Where your
                      // stock really lives", so the longer sentence was
                      // answering a question the label had asked.
                      { value: '', label: t("Here") },
                      { value: 'cardmarket', label: 'Cardmarket' },
                      { value: 'cardtrader', label: 'CardTrader' },
                    ]}
                    hint={t("If your real stock is on another marketplace, we say so on your listings — a card sold there can still be showing here until the next import.")} />
            <Input label={t("One line about the store")} value={form.about} maxLength={120}
                   placeholder={t("Singles wall, sealed product and four tables.")}
                   hint={t("Shown under the name on your page.")}
                   onChange={(e) => set('about', e.target.value)} />
          </Card>
        ) : null}

        {/*
          ONLY ONCE THERE IS A SHOP. During the application there is no account
          for a token to belong to, and asking for a credential from somebody
          who has not finished telling us their address is asking in the wrong
          order.
        */}
        {editing ? <SyncPanel /> : null}

        {editing || step === 2 ? (
          <>
            <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <span className="cbm-label">{t("Where the door is")}</span>
              {/* The pin is the trader's own point, never a typed address turned
                  into coordinates — the same rule as everywhere else here. */}
              <p style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)', margin: 0 }}>
                {pin
                  ? t("The pin is where you are set now. Stand in the store, or move your point first.")
                  : t("Set where you are first — that is where the store goes on the map.")}
              </p>
              {err('point') ? (
                <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--status-danger)' }}>{err('point')}</span>
              ) : null}
              {/*
                THE PIN AS IT IS, above the control that moves it. The owner,
                2026-10-19: "if we're going to show a 'Where the door is' with a
                pin, the set pin should be shown." Read-only; it follows the pin
                when the dialog saves a new one.
              */}
              <PinView value={pin} />
              {/*
                ALWAYS OFFERED, NOT ONLY WHEN THERE IS NO PIN.
                Reported: "there's a title that says 'Where the door is'. We
                should remove that or add a map so they can change the pin." It
                was the second thing: an existing shop saw a sentence about its
                pin and no way to move it, so the card described a setting it
                would not let anybody change.
              */}
              <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <Button size="sm" variant="outline" onClick={() => setLocating(true)}>
                  {pin ? t("Move the pin") : t("Set where you are")}
                </Button>
                {/*
                  AND THE ADDRESS CAN PUT IT THERE, which is the owner's own
                  reasoning: "a shop is very static usually, so the address
                  should be their point."

                  It does not contradict "never a typed address turned into
                  coordinates" — that rule protects a PERSON's home, which is
                  never shown and never geocoded. A shop's address is already
                  public on its own page, by a decision made for shops alone, so
                  the thing the rule protects is not at stake here.

                  Through our own /api/places, which proxies Nominatim: the
                  geocoder sees this server rather than the shopkeeper's browser,
                  it is rate-limited in one place, and the results agree with the
                  map tiles because both are OpenStreetMap.
                */}
                {form.address.trim().length >= 6 ? (
                  <Button size="sm" variant="ghost" disabled={finding}
                          onClick={findTheAddress}>
                    {finding ? t("Looking…") : t("Put the pin on my address")}
                  </Button>
                ) : null}
              </div>
            </Card>
            <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <span className="cbm-label">{t("Opening hours")}</span>
              {/* Not decoration: a trade arranged at a store has to fall inside
                  these, so what is typed here is what somebody can turn up to. */}
              <WeekHours value={form.hours} onChange={(v) => set('hours', v)} />
              {err('hours') ? (
                <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--status-danger)' }}>{err('hours')}</span>
              ) : null}
            </Card>
            <Card padding="var(--space-6)">
              {/* How many TABLES a shop has was asked here and is not asked any
                  more: "I don't think that's relevant whatsoever". Whether
                  somebody may meet at the counter is the question that changes
                  what the product does; the furniture is the shop's business. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-base)', color: 'var(--text-strong)' }}>
                    {t("Traders can meet at your counter")}
                  </div>
                  <div style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                    {t("You show up as a place to meet on trades nearby.")}
                  </div>
                </div>
                <Switch checked={form.counter} onChange={(v) => set('counter', v)} />
              </div>
            </Card>
          </>
        ) : null}

        {editing || step === 3 ? (
          <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <p style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)', margin: 0 }}>
              {t("A person checks the store exists before it goes live.")}
            </p>
            <Input label={t("Address")} value={form.address} error={err('address')}
                   hint={err('address') ? undefined : t("What somebody reads to find the door.")}
                   onChange={(e) => set('address', e.target.value)} />
            {/* The country code is asked for in the hint rather than met as an
                error, because a form that only tells you the rule after you
                break it is a form that made you break it. */}
            <Input label={t("Phone")} value={form.phone} placeholder="+34 968 12 34 56"
                   hint={t("With the country code, so anybody can ring it.")}
                   onChange={(e) => set('phone', e.target.value)} />
            <Input label={t("Website")} value={form.website}
                   onChange={(e) => set('website', e.target.value)} />

            {/*
              WHERE ELSE A SHOP CAN BE REACHED, which it could not say until
              now. The columns were always there — a shop is an account, and an
              account has had these since migration 044 — but the screen that
              sets them lives in Settings, and a shop cannot reach Settings any
              more. So they belong here, beside the phone, which is the same
              question asked three ways.

              EACH WITH ITS OWN SWITCH, because a number somebody gave us is not
              a number they agreed to publish. The same pair a person gets.
            */}
            <Input label={t("WhatsApp")} value={form.whatsapp} placeholder="+34 968 12 34 56"
                   hint={t("With the country code, like the phone.")}
                   onChange={(e) => set('whatsapp', e.target.value)} />
            {form.whatsapp.trim() ? (
              <Switch checked={form.whatsappVisible} onChange={(v) => set('whatsappVisible', v)}
                      label={t("Show WhatsApp on your page")} />
            ) : null}
            <Input label={t("Telegram")} value={form.telegram} placeholder="@tutienda"
                   onChange={(e) => set('telegram', e.target.value)} />
            {form.telegram.trim() ? (
              <Switch checked={form.telegramVisible} onChange={(v) => set('telegramVisible', v)}
                      label={t("Show Telegram on your page")} />
            ) : null}

            {/*
              AND THE NAME THE SHOP TRADES UNDER ON CARDMARKET.
              Not a way of being reached: it is where their stock already lives,
              which is the thing a game store told us decides whether they use
              us at all. Asked for now so the sync has an identity to work from
              the day it is built, rather than a migration and a form first.
            */}
            {/*
              NO PLACEHOLDER. Reported: "we should remove the DRAGONCT as a
              placeholder for cardmarket username. Let's invent something so
              random it's very unlikely it's a real username on Cardmarket or
              just keep it blank."

              Blank, rather than a cleverer invention. Any string short enough
              to read as an example is short enough to belong to somebody, and a
              made-up handle printed in a form is a real person's name as far as
              they are concerned — it would put a stranger's identity in front
              of every shop that opens this page. The label and the hint already
              say what goes here, which is what the placeholder was for.
            */}
            <Input label={t("Cardmarket username")} value={form.cardmarketUsername}
                   hint={t("The name your Cardmarket shop trades under, if you have one.")}
                   onChange={(e) => set('cardmarketUsername', e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))} />
          </Card>
        ) : null}

        {!editing && step === 4 ? (
          <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {[
              [t("The store"), `${form.name}${form.games.length ? ' · ' + GAMES.filter((g) => form.games.includes(g.id)).map((g) => g.label).join(', ') : ''}`, 1],
              [t("Hours"), hoursSummary(form.hours, t), 2],
              [t("Meeting people"), form.counter ? t("Traders can meet here") : t("No meeting at the counter"), 2],
              [t("Contact"), [form.address, form.phone].filter(Boolean).join(' · '), 3],
            ].map(([label, value, s], i, all) => (
              /* No rule under the last row: a line there separates it from
                 nothing, which is the one place a divider cannot be doing its
                 job. Reported with a screenshot. */
              <div key={label} style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'baseline',
                                        padding: 'var(--space-4) 0',
                                        borderBottom: i === all.length - 1 ? undefined : '1px solid var(--border-hairline)' }}>
                <span className="cbm-label" style={{ flex: '0 0 110px' }}>{label}</span>
                <span style={{ flex: 1, minWidth: 0, font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{value}</span>
                <button type="button" className="cbm-textlink" onClick={() => setStep(s)}>{t("Edit")}</button>
              </div>
            ))}
          </Card>
        ) : null}

        <div className="cbm-actionbar">
          <Button size="lg" disabled={busy} onClick={next}>
            {busy ? (editing ? t("Saving…") : t("Sending…"))
              : editing ? t("Save changes")
              : step === 4 ? t("Send") : t("Continue")}
          </Button>
          {!editing && step > 1 ? (
            <Button size="lg" variant="ghost" onClick={() => setStep(step - 1)}>{t("Back")}</Button>
          ) : null}
        </div>
      </div>

      <aside className="cbm-stack cbm-desk" style={{ position: 'sticky', top: 88, gap: 'var(--space-4)' }}>
        <span className="cbm-label">{t("How traders will see it")}</span>
        <Preview form={form} />
        <FluteDivider />
        {/*
          THE PITCH IS FOR SOMEBODY DECIDING WHETHER TO APPLY.
          Reported: it should go from /shop/dashboard?sec=shop. Right — a shop
          reading its own back office has already been checked, and a sentence
          explaining what being checked gets you is an advertisement aimed at
          the one person who no longer needs it. Same class as the "Open a store
          account" heading this screen used to show an existing shop.
        */}
        {editing ? null : (
          <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
            {t("A checked store trades under its own name, shows its address, and can be offered as a place to meet, if it allows it.")}
          </span>
        )}
      </aside>
      {/* Rendered unconditionally and opened by state, as on browse: the dialog
          owns the map, the permission prompt and the save, so mounting it only
          while open would rebuild all three every time somebody changes their
          mind. */}
      <ModalHost>
        {locating ? (
          <PinDialog value={pin} onClose={() => setLocating(false)}
                     onSave={(p) => { setPin(p); setLocating(false); }} />
        ) : null}
      </ModalHost>
    </div>
  );
}
